import React, { useState, useRef } from "react";
import { colors } from "../../constants/theme";
import { PLACEHOLDER_BASE64 } from "../../constants/data";
import type { Item } from "../../types";
import { getImageUrl, getImageId } from "../../utils/image";

interface ItemPreviewProps {
  item: Item;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.3;
const DEFAULT_ZOOM_OVER = 1; // Default zoom when mouse is over image
const DEFAULT_ZOOM_OUT = 1; // Default zoom when mouse is out

export const ItemPreview: React.FC<ItemPreviewProps> = ({ item }) => {
  const imageId = getImageId(item);
  const imageUrl = imageId ? getImageUrl(imageId) : null;
  const [zoomLevel, setZoomLevel] = useState<number>(DEFAULT_ZOOM_OUT);
  const [transformOrigin, setTransformOrigin] = useState<string>("center center");
  const [hasUserZoomed, setHasUserZoomed] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current || !imageRef.current) return;

    const container = containerRef.current;
    const image = imageRef.current;
    const rect = container.getBoundingClientRect();
    
    // Get mouse position relative to container
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Get image dimensions and position within container
    const imageRect = image.getBoundingClientRect();
    const imageX = imageRect.left - rect.left;
    const imageY = imageRect.top - rect.top;
    const imageWidth = imageRect.width;
    const imageHeight = imageRect.height;
    
    // Calculate position relative to image (0-1 range)
    const relativeX = (x - imageX) / imageWidth;
    const relativeY = (y - imageY) / imageHeight;
    
    // Only update transform origin if mouse is over the image
    if (relativeX >= 0 && relativeX <= 1 && relativeY >= 0 && relativeY <= 1) {
      // If user hasn't manually zoomed, set to default zoom over
      if (!hasUserZoomed && zoomLevel === DEFAULT_ZOOM_OUT) {
        setZoomLevel(DEFAULT_ZOOM_OVER);
      }
      
      // Calculate transform origin (percentage)
      const originX = relativeX * 100;
      const originY = relativeY * 100;
      setTransformOrigin(`${originX}% ${originY}%`);
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!containerRef.current || !imageRef.current) return;

    e.preventDefault();
    
    const container = containerRef.current;
    const image = imageRef.current;
    const rect = container.getBoundingClientRect();
    
    // Get mouse position relative to container
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Get image dimensions and position within container
    const imageRect = image.getBoundingClientRect();
    const imageX = imageRect.left - rect.left;
    const imageY = imageRect.top - rect.top;
    const imageWidth = imageRect.width;
    const imageHeight = imageRect.height;
    
    // Calculate position relative to image (0-1 range)
    const relativeX = (x - imageX) / imageWidth;
    const relativeY = (y - imageY) / imageHeight;
    
    // Only zoom if mouse is over the image
    if (relativeX >= 0 && relativeX <= 1 && relativeY >= 0 && relativeY <= 1) {
      // Mark that user has manually zoomed
      setHasUserZoomed(true);
      
      // Determine zoom direction (negative deltaY = scroll up = zoom in)
      const zoomDelta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoomLevel + zoomDelta));
      
      setZoomLevel(newZoom);
      
      // Update transform origin to mouse position
      const originX = relativeX * 100;
      const originY = relativeY * 100;
      setTransformOrigin(`${originX}% ${originY}%`);
    }
  };

  const handleMouseLeave = () => {
    // Reset zoom to 1x when mouse leaves
    setZoomLevel(DEFAULT_ZOOM_OUT);
    setTransformOrigin("center center");
    setHasUserZoomed(false);
  };

  const isZoomed = zoomLevel !== DEFAULT_ZOOM_OUT;

  return (
    <div 
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onWheel={handleWheel}
      style={{ 
        position: "relative", 
        borderRadius: 8, 
        overflow: "hidden", 
        border: `1px solid ${colors.border}`, 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        minHeight: 240, 
        maxHeight: "40vh", 
        background: "#fff",
        width: "100%",
        padding: 8,
        cursor: isZoomed ? "zoom-out" : "zoom-in"
      }}
    >
      <img 
        ref={imageRef}
        src={imageUrl || item.url || PLACEHOLDER_BASE64} 
        alt={item.filename || item.meta?.title || "preview"} 
        style={{ 
          maxWidth: "calc(100% - 16px)", 
          maxHeight: "calc(40vh - 16px)", 
          width: "auto", 
          height: "auto", 
          objectFit: "contain",
          display: "block",
          transform: `scale(${zoomLevel})`,
          transition: "transform 0.2s ease-out",
          transformOrigin: transformOrigin
        }} 
      />
    </div>
  );
};

